import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Link as MuiLink,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { JSX } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { BackButton } from "@/components/ui/back-button";
import { PageLoader } from "@/components/ui/page-loader";
import { marketingApi } from "./client.js";
import {
  ErrorBanner,
  PlatformPill,
  ScheduleStatusPill,
  SectionCardHeader,
  StatusPill,
  ThoughtPill,
  minutesAgo,
} from "./shared.js";

export function DraftDetailPage(): JSX.Element {
  const { id } = useParams();
  const draftId = Number(id);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const draft = useQuery({
    queryKey: ["marketing", "draft", draftId],
    queryFn: () => marketingApi.getDraft(draftId),
    enabled: Number.isFinite(draftId),
    retry: 0,
  });

  const thoughts = useQuery({
    queryKey: ["marketing", "draft-thoughts", draftId],
    queryFn: () => marketingApi.listThoughts({ draft_id: draftId, limit: 100 }),
    enabled: Number.isFinite(draftId),
    refetchInterval: 10_000,
    retry: 0,
  });

  const scheduledFor = useQuery({
    queryKey: ["marketing", "draft-schedule", draftId],
    queryFn: () => marketingApi.listScheduled({ draft_id: draftId, limit: 50 }),
    enabled: Number.isFinite(draftId),
    refetchInterval: 30_000,
    retry: 0,
  });

  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [hint, setHint] = useState("");
  const [autoPublishable, setAutoPublishable] = useState(false);
  const [suggestedAt, setSuggestedAt] = useState<string>("");

  useEffect(() => {
    if (draft.data) {
      setBody(draft.data.body);
      setTitle(draft.data.title ?? "");
      setAutoPublishable(draft.data.auto_publishable);
      setSuggestedAt(
        draft.data.suggested_post_at
          ? new Date(draft.data.suggested_post_at).toISOString().slice(0, 16)
          : "",
      );
    }
  }, [draft.data]);

  const review = useMutation({
    mutationFn: (action: "approve" | "reject" | "edit") => {
      const payload: Record<string, unknown> = { action };
      if (action === "edit") {
        payload.body = body;
        payload.title = title;
      }
      if (action === "approve") {
        payload.auto_publishable = autoPublishable;
        if (suggestedAt) {
          payload.suggested_post_at = new Date(suggestedAt).toISOString();
        }
      }
      if (notes) payload.notes = notes;
      return marketingApi.reviewDraft(draftId, payload as never);
    },
    onSuccess: (updated) => {
      qc.setQueryData(["marketing", "draft", draftId], updated);
      qc.invalidateQueries({ queryKey: ["marketing", "drafts"] });
      qc.invalidateQueries({ queryKey: ["marketing", "queue"] });
      qc.invalidateQueries({ queryKey: ["marketing", "overview"] });
      qc.invalidateQueries({ queryKey: ["marketing", "schedule"] });
    },
  });

  const regenerate = useMutation({
    mutationFn: () =>
      marketingApi.regenerateDraft(draftId, hint ? { reviewer_hint: hint } : {}),
    onSuccess: (updated) => {
      qc.setQueryData(["marketing", "draft", draftId], updated);
      setHint("");
    },
  });

  if (draft.isLoading) return <PageLoader />;
  if (draft.error) return <ErrorBanner error={draft.error} />;
  if (!draft.data) return <ErrorBanner error="draft not found" />;
  const d = draft.data;

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 14 }}>
          <MuiLink
            component={Link}
            to="/marketing/agent"
            underline="hover"
            sx={{ color: "text.disabled" }}
          >
            ← Drafts
          </MuiLink>
          <Box component="span" sx={{ color: "text.disabled" }}>/</Box>
          <Box component="span" sx={{ fontWeight: 500 }}>#{d.id}</Box>
          <Box component="span" sx={{ color: "text.disabled" }}>v{d.version}</Box>
          <Box component="span" sx={{ color: "text.disabled" }}>
            · priority {(d.priority_score * 100).toFixed(0)}%
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <PlatformPill platform={d.platform} />
          <StatusPill status={d.status} />
        </Box>
      </Box>

      <Card>
        <CardContent sx={{ display: "grid", gap: 2 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="(no title)"
            fullWidth
            inputProps={{ sx: { fontSize: 16, fontWeight: 500 } }}
          />

          <TextField
            label="Body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            rows={Math.max(8, Math.min(30, body.split("\n").length + 2))}
            fullWidth
            inputProps={{ sx: { fontFamily: "ui-monospace, monospace", fontSize: 14 } }}
          />

          {d.image_prompt ? (
            <Box>
              <Typography
                component="div"
                sx={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}
              >
                Image prompt
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  borderRadius: 1,
                  bgcolor: (t) => alpha(t.palette.text.secondary, 0.08),
                  p: 1,
                  fontSize: 14,
                }}
              >
                {d.image_prompt}
              </Typography>
            </Box>
          ) : null}

          <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { sm: "repeat(2, 1fr)" } }}>
            <Box component="label" sx={{ display: "flex", alignItems: "flex-start", gap: 1, fontSize: 14 }}>
              <Checkbox
                size="small"
                checked={autoPublishable}
                onChange={(e) => setAutoPublishable(e.target.checked)}
                sx={{ p: 0, mt: 0.25 }}
              />
              <Box component="span">
                <Box component="span" sx={{ fontWeight: 500 }}>Auto-publish on approve</Box>
                <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                  When approved, this draft will be picked up by the publisher
                  loop (subject to the publisher_enabled flag + per-platform
                  allow-list).
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography
                component="div"
                sx={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}
              >
                Suggested publish time
              </Typography>
              <TextField
                type="datetime-local"
                value={suggestedAt}
                onChange={(e) => setSuggestedAt(e.target.value)}
                fullWidth
                sx={{ mt: 0.5 }}
              />
              <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.disabled" }}>
                Leave blank to let the scheduler pick a slot from the
                platform's best-time window.
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography
              component="div"
              sx={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}
            >
              Reviewer notes
            </Typography>
            <TextField
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              fullWidth
              placeholder="Why approve / reject? (optional)"
              sx={{ mt: 0.5 }}
            />
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Button variant="contained" size="small" onClick={() => review.mutate("approve")} disabled={review.isPending}>
              Approve
            </Button>
            <Button variant="outlined" color="inherit" size="small" onClick={() => review.mutate("reject")} disabled={review.isPending}>
              Reject
            </Button>
            <Button variant="outlined" color="inherit" size="small" onClick={() => review.mutate("edit")} disabled={review.isPending}>
              Save edits
            </Button>
            <BackButton label="Back to queue" onClick={() => navigate("/marketing/agent")} />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { lg: "1fr 1fr" } }}>
        <Card>
          <SectionCardHeader
            title="Regenerate"
            subtitle="Re-run the AI Gateway. Bumps version, status returns to in_review."
          />
          <CardContent>
            <TextField
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              multiline
              rows={2}
              fullWidth
              placeholder="Optional steer: 'less hype', 'shorter', 'mention partner channel'…"
            />
            <Button
              variant="contained"
              size="small"
              sx={{ mt: 1 }}
              onClick={() => regenerate.mutate()}
              disabled={regenerate.isPending}
            >
              {regenerate.isPending ? "Asking gateway…" : "Regenerate"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <SectionCardHeader title="Why this?" subtitle="RAG sources the gateway used." />
          <CardContent>
            {d.generation_sources.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: "text.disabled" }}>No retrieved sources.</Typography>
            ) : (
              <Box component="ul" sx={{ display: "grid", gap: 0.5, fontSize: 12, listStyle: "none", m: 0, p: 0 }}>
                {d.generation_sources.map((s) => (
                  <Box component="li" key={s.index} sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                    <Box component="span" sx={{ fontFamily: "ui-monospace, monospace", color: "text.disabled" }}>[{s.index}]</Box>
                    <Box component="span" sx={{ flex: 1 }}>
                      <Box component="span" sx={{ fontWeight: 500 }}>{s.title || s.source_id}</Box>{" "}
                      <Box component="span" sx={{ color: "text.disabled" }}>· {s.source_app}/{s.source_type}</Box>
                    </Box>
                    <Box component="span" sx={{ fontFamily: "ui-monospace, monospace", color: "text.disabled" }}>d={s.distance.toFixed(3)}</Box>
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <Card>
        <SectionCardHeader
          title="Agent thinking for this draft"
          subtitle="Reasoning steps the agent recorded while generating this draft."
        />
        <CardContent>
          {thoughts.isLoading ? (
            <Skeleton variant="rounded" sx={{ height: 80 }} />
          ) : !thoughts.data?.length ? (
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
              No thoughts recorded for this draft. Older drafts predate the
              thinking trace feature.
            </Typography>
          ) : (
            <Box component="ol" sx={{ display: "grid", gap: 1, fontSize: 14, listStyle: "none", m: 0, p: 0 }}>
              {thoughts.data
                .slice()
                .reverse()
                .map((t) => (
                  <Box
                    component="li"
                    key={t.id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "max-content max-content 1fr",
                      alignItems: "baseline",
                      gap: 1.5,
                      borderBottom: 1,
                      borderColor: "divider",
                      py: 1,
                      "&:last-of-type": { borderBottom: 0 },
                    }}
                  >
                    <Box component="span" sx={{ fontFamily: "ui-monospace, monospace", fontSize: "11px", color: "text.disabled" }}>
                      {minutesAgo(t.created_at)}m
                    </Box>
                    <ThoughtPill type={t.thought_type} />
                    <Box component="span" sx={{ fontSize: 12 }}>{t.title}</Box>
                  </Box>
                ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <SectionCardHeader
          title="Publishing schedule"
          subtitle="Every scheduled_posts row that points at this draft."
        />
        <CardContent>
          {scheduledFor.isLoading ? (
            <Skeleton variant="rounded" sx={{ height: 80 }} />
          ) : !scheduledFor.data?.length ? (
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
              Not yet scheduled. The scheduler picks up approved drafts on
              the next pass.
            </Typography>
          ) : (
            <Box component="ul" sx={{ display: "grid", gap: 1, fontSize: 14, listStyle: "none", m: 0, p: 0 }}>
              {scheduledFor.data.map((p) => (
                <Box
                  component="li"
                  key={p.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                    pb: 1,
                    "&:last-of-type": { borderBottom: 0 },
                  }}
                >
                  <PlatformPill platform={p.platform} />
                  <Box component="span" sx={{ flex: 1, fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                    {new Date(p.scheduled_for).toLocaleString()}
                  </Box>
                  <ScheduleStatusPill status={p.status} />
                  {p.external_url && (
                    <MuiLink
                      href={p.external_url}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ fontSize: 12 }}
                    >
                      live
                    </MuiLink>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
